import React, { useEffect, useRef, useState } from "react";
import { animated } from "@react-spring/web";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ProjectsPresentation from "./ProjectsPresentation";
import {
    faC,
    faDatabase,
    faHashtag,
    faSchool,
} from "@fortawesome/free-solid-svg-icons";
import {
    faCss3Alt,
    faHtml5,
    faJava,
    faJs,
    faPhp,
    faPython,
    faReact
} from "@fortawesome/free-brands-svg-icons";
import "../styles/Presentation-style.css";

const Skill = ({ icon, name, isVisible }) => {
    const props = {
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(20px)",
    };

    return (
        <animated.div style={props} className="skill">
            <div className="skillName">
                <div id="language_logo">
                    {icon.map((icon, i) => (
                        <FontAwesomeIcon key={i} className="codeIcon" icon={icon} />
                    ))}
                </div>
                <p>{name}</p>
            </div>
        </animated.div>
    );
};

const Presentation = () => {
    const [isVisibleAbout, setIsVisibleAbout] = useState(false);
    const [isVisibleParcours, setIsVisibleParcours] = useState(false);
    const [isVisibleSkills, setIsVisibleSkills] = useState(false);
    const [isVisibleProjects, setIsVisibleProjects] = useState(false);
    const [isVisibleCv, setIsVisibleCv] = useState(false);

    const aboutSectionRef = useRef(null);
    const parcoursContainerRef = useRef(null);
    const skillsContainerRef = useRef(null);
    const projectsContainerRef = useRef(null);
    const cvContainerRef = useRef(null);

    useEffect(() => {
        const aboutSection = aboutSectionRef.current;
        const parcoursContainer = parcoursContainerRef.current;
        const skillsContainer = skillsContainerRef.current;
        const projectsContainer = projectsContainerRef.current;
        const cvContainer = cvContainerRef.current;


        const options = {
            root: null,
            rootMargin: "0px",
            threshold: 0.5,
        };

        const handleAboutSectionIntersection = (entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    entry.target.classList.add("animate", "exit");
                    setIsVisibleAbout(false);
                }
            });
        };

        const handleParcoursContainerIntersection = (entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    entry.target.classList.add("animate", "exit");
                    setIsVisibleParcours(false);
                }
            });
        };

        const handleSkillsContainerIntersection = (entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    entry.target.classList.add("animate", "exit");
                    setIsVisibleSkills(false);
                }
            });
        };

        const handleProjectsContainerIntersection = (entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    entry.target.classList.add("animate", "exit");
                    setIsVisibleProjects(false);
                }
            });
        }

        const handleCvContainerIntersection = (entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    entry.target.classList.add("animate", "exit");
                    setIsVisibleCv(false);
                }
            });
        }

        const aboutSectionObserver = new IntersectionObserver(
            handleAboutSectionIntersection,
            options
        );
        aboutSectionObserver.observe(aboutSection);

        const parcoursContainerObserver = new IntersectionObserver(
            handleParcoursContainerIntersection,
            options
        );
        parcoursContainerObserver.observe(parcoursContainer);

        const skillsContainerObserver = new IntersectionObserver(
            handleSkillsContainerIntersection,
            options
        );
        skillsContainerObserver.observe(skillsContainer);

        const projectsContainerObserver = new IntersectionObserver(
            handleProjectsContainerIntersection,
            options
        );
        projectsContainerObserver.observe(projectsContainer);

        const cvContainerObserver = new IntersectionObserver(
            handleCvContainerIntersection,
            options
        );
        cvContainerObserver.observe(cvContainer);

        return () => {
            aboutSectionObserver.disconnect();
            parcoursContainerObserver.disconnect();
            skillsContainerObserver.disconnect();
            projectsContainerObserver.disconnect();
            cvContainerObserver.disconnect();
        };
    }, []);
    return (
        <main id="main-about">
            <animated.section
                ref={aboutSectionRef}
                id="about"
                className={`about ${isVisibleAbout ? "animate exit" : ""}`}
            >
                <p>
                    Hi, I’m Rémy Kinzelin. I’m studying computer science at the University of Technology of Troyes (UTT) 🎓. I’m super into tech and love exploring how new tech can solve real-world problems.
                    <br/><br/>
                    After I finish my studies, I want to jump into software development—that’s where my passion lies! My dream is to lead a team and create something impactful together.
                    <br/><br/>
                    In my free time, I enjoy hands-on projects like internships, hackathons, or just working on my own to build skills and put my knowledge into practice.
                    <br/><br/>
                    Ultimately, I hope to make a difference in software and use tech to improve people’s lives.
                </p>
                <ul className="social-menu">
                    <li>
                        <a
                            id="profile-link"
                            href="https://github.com/SLATTY54"
                            target="_blank"
                            rel={"noreferrer"}
                        >
                            <i className="fab fa-github fa-2x"></i>
                        </a>
                    </li>
                    <li>
                        <a href="https://twitter.com/Kinzelin2" target="_blank" rel={"noreferrer"}>
                            <i className="fab fa-twitter fa-2x"></i>
                        </a>
                    </li>
                    <li>
                        <a
                            href="https://www.linkedin.com/in/r%C3%A9my-kinzelin-129a1b1a0/"
                            target="_blank"
                            rel={"noreferrer"}
                        >
                            <i className="fab fa-linkedin fa-2x"></i>
                        </a>
                    </li>
                </ul>
            </animated.section>

            <animated.div
                ref={parcoursContainerRef}
                className={`parcours ${isVisibleParcours ? "animate exit" : ""}`}
            >
                <div className="myInterest">
                    <h3>Interest</h3>
                    <ul>
                        <li>Game development</li>
                        <li>Sport (Soccer during 13 years)</li>
                        <li>Video game</li>
                        <li>Movie</li>
                        <li>Music</li>
                        <li>Programming</li>
                    </ul>
                </div>
                <div className="education">
                    <h3>Education</h3>
                    <ul>
                        <li>
                            <FontAwesomeIcon className="schoolIcon" icon={faSchool}/>
                            <div className="description">
                                <div className="date">
                                    <p>2023 - Current</p>
                                </div>
                                <div className="course">
                                    <p>
                                        Master's Degree in Computer Science and Information Systems
                                    </p>
                                </div>
                                <div className="location">
                                    <p>
                                        University of Technology of Troyes (UTT), France
                                    </p>
                                </div>
                            </div>
                        </li>
                        <li>
                            <FontAwesomeIcon className="schoolIcon" icon={faSchool}/>
                            <div className="description">
                                <div className="date">
                                    <p>2021 - 2023</p>
                                </div>
                                <div className="course">
                                    <p>Bachelor of Technology in Computer Science</p>
                                </div>
                                <div className="location">
                                    <p>IUT Charlemagne of Nancy, France</p>
                                </div>
                            </div>
                        </li>
                    </ul>
                </div>
            </animated.div>
            <animated.div
                ref={skillsContainerRef}
                className={`skills_container ${
                    isVisibleSkills ? "animate exit" : ""
                }`}
            >
                <div className="skills-content">
                    <h3>Skills</h3>
                    <div className="skills">
                        <Skill
                            icon={[faHtml5, faCss3Alt]}
                            name="HTML/CSS"
                            isVisible={isVisibleSkills}
                        />
                        <Skill icon={[faJava]} name="Java" isVisible={isVisibleSkills}/>
                        <Skill icon={[faPython]} name="Python" isVisible={isVisibleSkills}/>
                        <Skill
                            icon={[faJs]}
                            name="JavaScript"
                            isVisible={isVisibleSkills}
                        />
                        <Skill icon={[faPhp]} name="PHP" isVisible={isVisibleSkills}/>
                        <Skill
                            icon={[faReact]}
                            name="React"
                            isVisible={isVisibleSkills}
                        />
                        <Skill icon={[faDatabase]} name="SQL" isVisible={isVisibleSkills}/>
                        <Skill icon={[faC, faHashtag]} name="C#" isVisible={isVisibleSkills}/>
                    </div>
                </div>

                <div className={"software_skills"}>
                    <h3>Software</h3>
                    <div className="marquee marquee--8">
                        <img className="marquee__item"
                             src={require('../images/softwaresLogos/intellij_macos_bigsur_icon_190061.png')} width="100"
                             height="100" alt=""/>
                        <img className="marquee__item"
                             src={require("../images/softwaresLogos/intellij_phpstorm_macos_bigsur_icon_190057.png")} width="100"
                             height="100" alt=""/>
                        <img className="marquee__item"
                             src={require("../images/softwaresLogos/intellij_webstorm_macos_bigsur_icon_190053.png")} width="100"
                             height="100" alt=""/>
                        <img className="marquee__item"
                             src={require("../images/softwaresLogos/rider_93571.png")} width="100"
                             height="100" alt=""/>
                        <img className="marquee__item"
                             src={require("../images/softwaresLogos/terminal.png")} width="100"
                             height="100" alt=""/>
                        <img className="marquee__item"
                             src={require("../images/softwaresLogos/github-mark-white.png")} width="100"
                             height="100" alt=""/>
                        <img className="marquee__item"
                             src={require("../images/softwaresLogos/unityIcon.png")} width="250"
                             height="50" alt=""/>
                    </div>
                </div>
            </animated.div>
            <animated.div ref={projectsContainerRef} id={"projects"}
                          className={`projects ${isVisibleProjects ? "animate exit" : ""}`}>
                <ProjectsPresentation/>
            </animated.div>

            <animated.div ref={cvContainerRef} id={"myCv"} className={`myCv ${isVisibleCv ? "animate exit" : ""}`}>
                <h3>My CV</h3>
                <div>
                    <a href={require("../images/cv/cv_KinzelinRemyEN.pdf")} target="_blank">English CV</a>
                    <a href={require("../images/cv/cv_KinzelinRemyFR.pdf")} target="_blank">French CV</a>
                </div>
            </animated.div>

        </main>
);
};

export default Presentation;